<script setup>
import { ref, computed, onMounted, getCurrentInstance, watch } from 'vue';
import { getMatchesByDate, loadingDayMatch } from './matchDataUtils.js';
import DateCalendar from './DateCalendar.vue';

const isSelectingDate = ref(true);
const activeDateId = ref('');
const props = defineProps({
  loading: Boolean
});
const { proxy } = getCurrentInstance();
const isLoading = computed(
  () => proxy.$stores.match.state?.list_all?.loading || props.loading
);

// Génération de 30 jours avant + aujourd'hui + 30 jours après
const generateAllDates = () => {
  const allDates = [];
  const today = new Date(); // 9 novembre 2024
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const months = [
    'jan',
    'fév',
    'mar',
    'avr',
    'mai',
    'juin',
    'juil',
    'août',
    'sept',
    'oct',
    'nov',
    'déc'
  ];

  // 30 jours avant aujourd'hui
  for (let i = 30; i >= 1; i--) {
    const pastDate = new Date(today);
    pastDate.setDate(today.getDate() - i);

    allDates.push({
      id: `past-${i}`,
      dateName: dayNames[pastDate.getDay()],
      dateDesc: `${pastDate.getDate()} ${months[pastDate.getMonth()]}`,
      isPast: true,
      isToday: false,
      isFuture: false
    });
  }

  // Aujourd'hui
  allDates.push({
    id: 'today',
    dateName: "Aujourd'hui",
    dateDesc: `${today.getDate()} ${months[today.getMonth()]}`,
    isPast: false,
    isToday: true,
    isFuture: false
  });

  // 30 jours après aujourd'hui
  for (let i = 1; i <= 30; i++) {
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + i);

    allDates.push({
      id: `future-${i}`,
      dateName: dayNames[futureDate.getDay()],
      dateDesc: `${futureDate.getDate()} ${months[futureDate.getMonth()]}`,
      isPast: false,
      isToday: false,
      isFuture: true
    });
  }

  return allDates;
};

const dates = ref(generateAllDates());

// Computed property pour toutes les données de matches

const allMatchesData = computed(() => {
  return dates.value.map((dateData) => ({
    ...dateData,
    dayMatches: getMatchesByDate(
      dateData.id,
      proxy.$stores.match.state?.list_all?.list
    )
  }));
});

const onDateSelectFromCalendar = (date) => {
  const today = new Date(); // Base: 10 oct 2024
  const targetDate = new Date(date);
  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  let dateId;
  if (diffDays < 0) {
    dateId = `past-${Math.abs(diffDays)}`;
  } else if (diffDays === 0) {
    dateId = 'today';
  } else {
    dateId = `future-${diffDays}`;
  }

  selectDate(dateId);
};

// Fonction pour scroller vers une date spécifique
const scrollToDate = (dateId, instantScroll) => {
  const element = document.getElementById(`matches-${dateId}`);
  if (element) {
    element.scrollIntoView({
      behavior: instantScroll ? 'instant' : 'smooth',
      block: 'start'
    });
  }
  return !!element;
};

// Fonction pour activer une date (quand on clique dessus)
const selectDate = (dateId, instantScroll) => {
  isSelectingDate.value = true;
  activeDateId.value = dateId;
  let done = scrollToDate(dateId, instantScroll);
  setTimeout(
    () => {
      isSelectingDate.value = false;
      centerDate(dateId, instantScroll);
      if (done) {
        updateActiveDateOnScroll();
      }
    },
    instantScroll ? 0 : 1000
  );
};

// Fonction pour centrer une date dans la barre de dates horizontale
const centerDate = (dateId, instantScroll) => {
  const dateElement = document.getElementById(dateId);
  dateElement?.scrollIntoView({
    inline: 'center',
    behavior: instantScroll ? 'instant' : 'smooth'
  });
};

// Fonction pour mettre à jour la date active en fonction du scroll
const updateActiveDateOnScroll = () => {
  if (isSelectingDate.value) return;

  const scrollMiddle = document.body.offsetHeight / 3;

  let closestSection = null;
  let minDistance = Infinity;

  // Parcourir toutes les sections de dates (avec et sans matches) pour trouver la plus proche du centre du viewport
  for (const dateData of allMatchesData.value) {
    const element = document.getElementById(`matches-${dateData.id}`);
    if (element) {
      const elementRect = element.getBoundingClientRect();

      const scrollMargin = 100; // 9rem en pixels (changé)
      const elementTopRelative = elementRect.top - scrollMargin;
      const elementMiddle = elementTopRelative + elementRect.height / 3;

      const distance = Math.abs(elementMiddle - scrollMiddle);

      if (distance < minDistance) {
        minDistance = distance;
        closestSection = dateData.id;
      }
    }
  }

  // Mettre à jour la date active et centrer dans la barre de dates
  if (closestSection && closestSection !== activeDateId.value) {
    activeDateId.value = closestSection;
    centerDate(closestSection);
  }
};

// Throttle pour optimiser les performances du scroll
let scrollTimeout = null;
const throttledScrollHandler = () => {
  if (scrollTimeout) {
    clearTimeout(scrollTimeout);
  }
  scrollTimeout = setTimeout(updateActiveDateOnScroll, 100);
};

watch(
  () => isLoading.value,
  (newVal) => {
    if (newVal == false) {
      setTimeout(() => {
        selectDate('today', true);
      }, 100);
    }
  }
);

onMounted(() => {
  selectDate('today', true);
});
</script>

<style scoped>
.shimmerElement {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>

<template>
  <div class="h-full overflow-hidden">
    <VBusHelper :emitOnMounted="$constants.EVENTS.MATCH.LIST_ALL.REFRESH" v-if="!props.loading" />

    <div class="bg-primary fixed top-0 z-10 w-full">
      <div class="mobile-status-padding"></div>
      <div class="px-4 overflow-x-hidden w-full h-24">
        <div class="flex gap-4 items-center h-full">
          <div class="w-8 h-8 min-w-8 flex items-center justify-center">
            <button class="w-full h-full" @click="$emit('backClicked')">
              <ColorImg
                src="/images/icons/arrow-up.svg"
                class="w-full h-full bg-white -rotate-90"
                alt="Calendar"
              />
            </button>
          </div>
          <div class="h-12 border-l border-white/20"></div>
          <div class="w-full overflow-auto flex gap-6 py-3">
            <div class="min-w-[50vw]"></div>
            <div
              v-for="(date, i) in dates"
              :key="i"
              :id="date.id"
              class="flex-shrink-0 cursor-pointer transition-all"
              @click="selectDate(date.id)"
            >
              <div
                class="flex flex-col justify-center items-center p-2 rounded-lg min-w-[60px]"
                :class="{
                  ' text-white scale-110': activeDateId === date.id,
                  'text-white/50': date.isPast && activeDateId !== date.id,
                  'text-white/80 ': !date.isPast && activeDateId !== date.id
                }"
              >
                <div class="font-bold uppercase">
                  {{ date.dateName }}
                </div>
                <div class="uppercase">
                  {{ date.dateDesc }}
                </div>
              </div>
            </div>
            <div class="min-w-[50vw]"></div>
          </div>
          <div class="h-12 border-l border-white/20"></div>
          <div class="w-8 h-8 min-w-8 flex items-center justify-center">
            <VDropdown
              :distance="8"
              theme="tooltip-light"
              placement="bottom-end"
              :auto-hide="true"
              :delay="{ show: 0, hide: 100 }"
            >
              <button class="w-full h-full" title="Ouvrir le calendrier">
                <ColorImg
                  src="/images/match/calendar.svg"
                  class="w-full h-full bg-white"
                  alt="Calendar"
                />
              </button>

              <template #popper="{ hide }">
                <DateCalendar
                  @select="
                    (date) => {
                      onDateSelectFromCalendar(date);
                      hide();
                    }
                  "
                />
              </template>
            </VDropdown>
          </div>
        </div>
      </div>
    </div>
    <div class="h-full overflow-auto" @scroll="throttledScrollHandler">
      <div class="">
        <div class="h-[8rem]"></div>
        <div class="h-[30vh]" v-show="!isLoading"></div>
        <!-- {{ $stores.match.state?.list_all }} -->
        <div class="flex flex-col">
          <!-- État de chargement avec shimmer blocks -->
          <template v-if="isLoading">
            <!-- Simuler plusieurs blocs de chargement -->
            <div
              v-for="i in 5"
              :key="`shimmer-${i}`"
              class="scroll-m-[9rem] pb-32"
            >
              <!-- Header shimmer -->
              <div class="sticky top-0 z-[5] flex flex-col items-center mb-4">
                <div class="w-24 h-0.5 shimmerElement rounded-full mb-2"></div>
                <div class="w-48 h-6 shimmerElement rounded-lg mb-2"></div>
                <div class="w-32 h-4 shimmerElement rounded-full"></div>
                <div class="w-24 h-0.5 shimmerElement rounded-full mt-2"></div>
              </div>

              <!-- Matches shimmer blocks -->
              <div class="px-4 space-y-4">
                <!-- Simuler 2-3 cartes de match par jour -->
                <div
                  v-for="j in 2"
                  :key="`shimmer-match-${i}-${j}`"
                  class="overflow-hidden"
                >
                  <div class="p-4 space-y-3">
                    <!-- Header catégorie shimmer -->
                    <div class="flex items-center gap-4">
                      <div class="w-8 h-8 shimmerElement rounded-full"></div>
                      <div class="flex-1 space-y-2">
                        <div class="w-48 h-4 shimmerElement rounded-lg"></div>
                        <div class="w-32 h-3 shimmerElement rounded-full"></div>
                      </div>
                      <div class="w-5 h-5 shimmerElement rounded-full"></div>
                    </div>

                    <!-- Matches shimmer -->
                    <div class="space-y-2 pt-2">
                      <div
                        v-for="k in 2"
                        :key="`shimmer-team-${i}-${j}-${k}`"
                        class="flex items-center gap-3"
                      >
                        <div class="w-12 h-4 shimmerElement rounded-full"></div>
                        <div class="flex-1">
                          <div
                            class="w-24 h-3 shimmerElement rounded-full mb-1"
                          ></div>
                          <div
                            class="w-16 h-3 shimmerElement rounded-full"
                          ></div>
                        </div>
                        <div class="w-8 h-4 shimmerElement rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Contenu réel (une fois chargé) -->
          <template v-else>
            <template
              v-for="(dateData, dateIndex) in allMatchesData"
              :key="dateIndex"
            >
              <div :id="`matches-${dateData.id}`" class="scroll-m-[9rem] pb-32">
                <!-- Header de la date -->
                <div class="sticky top-0 flex flex-col items-center mb-4">
                  <div class="border-b w-24 border-black mb-2"></div>
                  <div class="text-2xl font-bold">
                    {{ dateData.dateName }}, {{ dateData.dateDesc }}
                  </div>
                  <div class="opacity-90">
                    <span v-if="dateData.dayMatches.length > 0">
                      {{ dateData.dayMatches.length }} compétition{{
                        dateData.dayMatches.length > 1 ? 's' : ''
                      }}
                    </span>
                    <span v-else class="text-gray-500">
                      Aucun match prévu
                    </span>
                  </div>
                  <div class="border-b w-24 border-black mt-2"></div>
                </div>

                <!-- Contenu : matches ou message "pas de match" -->
                <div
                  v-if="dateData.dayMatches.length > 0"
                  class="px-4 flex flex-col gap-8"
                >
                  <div
                    v-for="(categoryData, categoryIndex) in dateData.dayMatches"
                    :key="categoryIndex"
                  >
                    <div
                      class="bg-white overflow-hidden"
                      @click="
                        $bus.emit(
                          $constants.EVENTS.MATCH.SHOW_COMPETITION_DETAIL,
                          categoryData
                        )
                      "
                    >
                      <!-- Header catégorie -->
                      <div
                        class="flex bg-[#E9EEF2] items-center overflow-hidden gap-5 p-4"
                      >
                        <div class="w-8 h-8 min-w-8">
                          <ImgComponent
                            :src="categoryData.category.logo"
                            fallback="/images/match/terrain.svg"
                            class="w-full h-full"
                            alt=""
                          />
                        </div>
                        <div class="w-full overflow-hidden">
                          <div
                            class="truncate w-full text-lg font-bold text-primary-800"
                          >
                            {{ categoryData.category.name }}
                          </div>
                          <div class="truncate w-full text-primary-600">
                            {{ categoryData.category.round }}
                          </div>
                        </div>
                        <div
                          class="w-5 h-5 min-w-5 flex items-center justify-center"
                        >
                          <ColorImg
                            src="/images/icons/right-arrow.svg"
                            class="w-full h-full bg-gray-500"
                            alt=""
                          />
                        </div>
                      </div>

                      <!-- Matches de la catégorie -->
                      <div class="divide-y divide-gray-200">
                        <MatchesPresentation
                          v-for="(match, matchIndex) in categoryData.matches"
                          :key="matchIndex"
                          :match="match"
                          @click.stop="
                            $bus.emit(
                              $constants.EVENTS.MATCH.SHOW_MATCH_DETAIL,
                              match
                            )
                          "
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Message pour les jours sans match -->
                <div v-else class="px-4">
                  <!-- Simuler 2-3 cartes de match par jour -->
                  <div
                    class="flex space-y-4 flex-col"
                    v-if="
                      loadingDayMatch(
                        dateData.id,
                        $stores.match.state?.list_all?.list || []
                      )?.isLoading
                    "
                  >
                    <div v-for="j in 2" class="overflow-hidden">
                      <div class="p-4 space-y-3">
                        <!-- Header catégorie shimmer -->
                        <div class="flex items-center gap-4">
                          <div
                            class="w-8 h-8 shimmerElement rounded-full"
                          ></div>
                          <div class="flex-1 space-y-2">
                            <div
                              class="w-48 h-4 shimmerElement rounded-lg"
                            ></div>
                            <div
                              class="w-32 h-3 shimmerElement rounded-full"
                            ></div>
                          </div>
                          <div
                            class="w-5 h-5 shimmerElement rounded-full"
                          ></div>
                        </div>

                        <!-- Matches shimmer -->
                        <div class="space-y-2 pt-2">
                          <div
                            v-for="k in 2"
                            :key="`shimmer-team-${dateIndex}-${j}-${k}`"
                            class="flex items-center gap-3"
                          >
                            <div
                              class="w-12 h-4 shimmerElement rounded-full"
                            ></div>
                            <div class="flex-1">
                              <div
                                class="w-24 h-3 shimmerElement rounded-full mb-1"
                              ></div>
                              <div
                                class="w-16 h-3 shimmerElement rounded-full"
                              ></div>
                            </div>
                            <div
                              class="w-8 h-4 shimmerElement rounded-full"
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="space-y-4">
                    <div
                      class="border-2 border-dashed border-gray-300 bg-gray-50/30 p-12 text-center"
                    >
                      <div
                        class="flex flex-col items-center justify-center space-y-4"
                      >
                        <!-- Icône de calendrier ou match -->
                        <div
                          class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center"
                        >
                          <svg
                            class="w-8 h-8 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>

                        <!-- Texte explicatif -->
                        <div class="space-y-2">
                          <h3 class="text-lg font-semibold text-gray-700">
                            Aucun match ce jour
                          </h3>
                          <p class="text-gray-500 text-sm max-w-xs mx-auto">
                            Les compétitions reprennent demain avec de nouveaux
                            affrontements passionnants !
                          </p>
                        </div>

                        <!-- Indicateur visuel -->
                        <div
                          class="flex items-center space-x-2 text-xs text-gray-400"
                        >
                          <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span>Journée de repos</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </template>
        </div>
        <!-- <div class="h-[8rem]"></div> -->
        <div class="h-[28vh]" v-show="!isLoading"></div>
      </div>
    </div>
  </div>
</template>
